/**
 * useAdmin Hook
 *
 * Checks if the current user is an admin.
 * Admins bypass all subscription checks.
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/firebase';
import { adminsService } from '@/lib/firebase/admins';

interface UseAdminReturn {
  isAdmin: boolean;
  isLoading: boolean;
  error: Error | null;
}

export function useAdmin(): UseAdminReturn {
  const { firebaseUser } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    async function checkAdmin() {
      if (!firebaseUser?.email) {
        setIsAdmin(false);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const adminStatus = await adminsService.isAdmin(firebaseUser.email);
        setIsAdmin(adminStatus);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to check admin status'));
        setIsAdmin(false);
      } finally {
        setIsLoading(false);
      }
    }

    checkAdmin();
  }, [firebaseUser?.email]);

  return { isAdmin, isLoading, error };
}
